import { useMsal } from "@azure/msal-react";
import { get } from "lodash";
import { GetStaticProps, GetStaticPropsContext } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useRouter } from "next/router";
import { ConfirmDialog } from "primereact/confirmdialog";
import { useResizeListener } from "primereact/hooks";
import { ReactNode, useEffect, useState } from "react";
import useSWRMutation from "swr/mutation";
import { useCurrentUser } from "../common/api/useCurrentUser";
import { DEFAULT_LOCALE } from "../common/constants";
import { fetcher } from "../common/hooks";
import { msalInstance } from "../common/services/auth";
import { useRedirectStore } from "../common/store/useRedirectStore";
import { useLiveAnnouncementsStream } from "../features/announcements/api";
import { GlobalAnnouncement } from "../features/announcements/components";
import { INSTALLATION_ROUTES } from "../features/installation/constants/router.constants";
import { Header as InstallationHeader } from "../features/installation/features/layout/components/Header";
import { Header } from "../features/layout/components/Header";
import { Sidebar } from "../features/layout/components/Sidebar";
import { SetUpSpacesFlow } from "../features/set-up-spaces-flow/components/SetUpSpacesFlow";
import { useSetUpSpacesStore } from "../features/set-up-spaces-flow/store";
import { EmailNotificationModal } from "../features/user-notifications/components/EmailNotificationModal";

interface FrameProps {
    children?: ReactNode;
}

const getRedirectUrl = (
    url: string,
    {
        arg,
    }: {
        arg: {
            routeTo: string;
        };
    }
) => fetcher(`${url}?returnTo=${arg.routeTo}`);

const Frame = (props: FrameProps) => {
    const { children } = props;

    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(true);

    const { externalRedirect, setExternalRedirect } = useRedirectStore(),
        { reset } = useSetUpSpacesStore();

    const { user } = useCurrentUser(),
        router = useRouter(),
        { instance } = useMsal();

    useLiveAnnouncementsStream({});

    const activeAccount = instance.getActiveAccount(),
        currentLocale = router.locale || DEFAULT_LOCALE,
        logoutRequest = {
            postLogoutRedirectUri: `/${currentLocale}/spaces`,
            idTokenHint: activeAccount?.idToken,
            mainWindowRedirectUri: `/${currentLocale}/spaces`,
            extraQueryParameters: {
                ui_locales: currentLocale,
            },
        };

    const [bindWindowResizeListener, unbindWindowResizeListener] =
        useResizeListener({
            listener: (event: Event) => {
                const innerWidth = get(event, "currentTarget.innerWidth");
                if (innerWidth && innerWidth > 767) {
                    setIsMobileSidebarOpen(true);
                }
            },
        });

    useEffect(() => {
        bindWindowResizeListener();

        return () => {
            unbindWindowResizeListener();
        };
    }, [bindWindowResizeListener, unbindWindowResizeListener]);

    const onLogoutClick = () => {
        reset();
        msalInstance.logoutRedirect(logoutRequest);
    };

    const { trigger: triggerGetRedirectUrl } = useSWRMutation(
        "/api/support-management/v1/redirect",
        getRedirectUrl,
        {
            onSuccess: (data: { url?: string }) => {
                if (data.url) {
                    location.replace(data.url);
                }
            },
        }
    );

    useEffect(() => {
        // set preferred language
        if (
            user?.languageCode &&
            user.languageCode.toLowerCase() !== router?.locale?.toLowerCase()
        ) {
            const { pathname, asPath, query } = router;
            router.push({ pathname, query }, asPath, {
                locale: user.languageCode,
            });
        }
    }, [user?.languageCode, router]);

    useEffect(() => {
        if (externalRedirect) {
            setExternalRedirect(null);
            triggerGetRedirectUrl({ routeTo: externalRedirect });
        }
    }, [externalRedirect, setExternalRedirect, triggerGetRedirectUrl]);

    const isInstallation = Object.values(INSTALLATION_ROUTES).includes(
        router.pathname
    );

    return isInstallation ? (
        <div
            className={
                "flex flex-col h-[100dvh] overflow-hidden bg-surface-default"
            }
        >
            <GlobalAnnouncement />
            <div className="flex flex-col flex-1 w-full overflow-auto min-h-0">
                <InstallationHeader />
                <main className={"grow overflow-y-auto px-4 py-2"}>
                    {children}
                </main>
                <EmailNotificationModal />
                <ConfirmDialog />
                <SetUpSpacesFlow />
            </div>
        </div>
    ) : (
        <div className="flex flex-col h-[100dvh]">
            <GlobalAnnouncement />
            <div className="flex flex-1 min-h-0 overflow-hidden bg-surface-8">
                <Sidebar
                    isOpen={isMobileSidebarOpen}
                    onCloseIconClick={() => setIsMobileSidebarOpen(false)}
                />
                <div className="flex flex-col flex-1 w-full overflow-auto">
                    <Header
                        onOpenMenuClick={() => setIsMobileSidebarOpen(true)}
                        showMenuIcon={!isMobileSidebarOpen}
                        onLogoutClick={onLogoutClick}
                    />
                    <main className="grow overflow-y-auto">{children}</main>
                    <EmailNotificationModal />
                    <ConfirmDialog />
                    <SetUpSpacesFlow />
                </div>
            </div>
        </div>
    );
};

export const getStaticProps: GetStaticProps = async (
    ctx: GetStaticPropsContext
) => {
    return {
        props: {
            ...(await serverSideTranslations(ctx.locale as string, [
                "common",
                "spaces",
                "language",
                "toast",
            ])),
        },
    };
};

export default Frame;
